import React, { useState } from 'react';
import { useAuthenticatedFilePreview } from '../../hooks/useAuthenticatedFilePreview';
import {
  Box,
  Popover,
  Typography,
  Avatar,
  CircularProgress,
  Divider,
  Stack,
  Link as MuiLink,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { mitarbeiterService } from '../../services/mitarbeiterService';
import type { MitarbeiterOeffentlichesProfil } from '../../types';

const ABS_ART_LABEL: Record<string, string> = {
  studium: 'Studium',
  ausbildung: 'Ausbildung',
  zertifikat: 'Zertifikat',
  sonstiges: 'Sonstiges',
};

const NIVEAU_LABEL: Record<string, string> = {
  grundkenntnisse: 'Grundkenntnisse',
  fortgeschritten: 'Fortgeschritten',
  expertise: 'Expertise',
};

export interface MitarbeiterKurzprofilLinkProps {
  mitarbeiterId: number;
  children: React.ReactNode;
  sx?: object;
}

export const MitarbeiterKurzprofilLink: React.FC<MitarbeiterKurzprofilLinkProps> = ({
  mitarbeiterId,
  children,
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [profil, setProfil] = useState<MitarbeiterOeffentlichesProfil | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const avatarPreviewUrl = useAuthenticatedFilePreview(profil?.avatar_file_id ?? null);

  const open = Boolean(anchorEl);

  const handleOpen = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setLoading(true);
    setFailed(false);
    setProfil(null);
    try {
      const p = await mitarbeiterService.getOeffentlichesProfil(mitarbeiterId);
      setProfil(p);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setProfil(null);
  };

  const hasPublicDetails = (p: MitarbeiterOeffentlichesProfil) => {
    return !!(
      p.handynummer ||
      p.festnetz ||
      p.kontakt_zeitfenster ||
      p.adresse_zeile1 ||
      p.plz ||
      p.ort ||
      p.land ||
      p.geburtsdatum ||
      p.profil_beschreibung ||
      (p.sprachen && p.sprachen.length) ||
      p.zeitzone ||
      p.beruflicher_titel ||
      p.link_linkedin ||
      p.link_website ||
      (p.profil_abschluesse && p.profil_abschluesse.length) ||
      (p.profil_faehigkeiten && p.profil_faehigkeiten.length) ||
      p.avatar_file_id
    );
  };

  return (
    <>
      <Box
        component="span"
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '3px',
          ...sx,
        }}
      >
        {children}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 380, p: 2 } } }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, minWidth: 200 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {failed && !loading && (
          <Typography variant="body2" color="text.secondary">
            Kurzprofil konnte nicht geladen werden.
          </Typography>
        )}
        {profil && !loading && (
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {profil.avatar_file_id ? (
                <Avatar src={avatarPreviewUrl} alt="" sx={{ width: 48, height: 48 }}>
                  {!avatarPreviewUrl ? <PersonOutlineIcon /> : null}
                </Avatar>
              ) : (
                <Avatar sx={{ width: 48, height: 48 }}>
                  <PersonOutlineIcon />
                </Avatar>
              )}
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {profil.vorname} {profil.nachname}
                </Typography>
                {!hasPublicDetails(profil) && (
                  <Typography variant="caption" color="text.secondary">
                    Keine öffentlichen Profildaten hinterlegt.
                  </Typography>
                )}
              </Box>
            </Stack>

            {profil.beruflicher_titel && (
              <Typography variant="body2" color="text.secondary">
                {profil.beruflicher_titel}
              </Typography>
            )}

            {(profil.link_linkedin || profil.link_website) && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {profil.link_linkedin && (
                  <MuiLink href={profil.link_linkedin} target="_blank" rel="noopener noreferrer" variant="body2">
                    LinkedIn
                  </MuiLink>
                )}
                {profil.link_website && (
                  <MuiLink href={profil.link_website} target="_blank" rel="noopener noreferrer" variant="body2">
                    Website
                  </MuiLink>
                )}
              </Stack>
            )}

            {(profil.handynummer || profil.festnetz || profil.kontakt_zeitfenster) && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Kontakt
                </Typography>
                {profil.handynummer && <Typography variant="body2">Mobil: {profil.handynummer}</Typography>}
                {profil.festnetz && <Typography variant="body2">Tel.: {profil.festnetz}</Typography>}
                {profil.kontakt_zeitfenster && (
                  <Typography variant="body2">Erreichbarkeit: {profil.kontakt_zeitfenster}</Typography>
                )}
              </>
            )}

            {(profil.zeitzone || (profil.sprachen && profil.sprachen.length > 0)) && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Erreichbarkeit
                </Typography>
                {profil.zeitzone && <Typography variant="body2">Zeitzone: {profil.zeitzone}</Typography>}
                {profil.sprachen && profil.sprachen.length > 0 && (
                  <Typography variant="body2">Sprachen: {profil.sprachen.join(', ')}</Typography>
                )}
              </>
            )}

            {(profil.adresse_zeile1 || profil.plz || profil.ort || profil.land) && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Adresse
                </Typography>
                <Typography variant="body2" component="div">
                  {profil.adresse_zeile1 && <span>{profil.adresse_zeile1}<br /></span>}
                  {(profil.plz || profil.ort) && (
                    <span>
                      {profil.plz} {profil.ort}
                      <br />
                    </span>
                  )}
                  {profil.land}
                </Typography>
              </>
            )}

            {profil.geburtsdatum && (
              <>
                <Divider />
                <Typography variant="body2">Geburtsdatum: {profil.geburtsdatum}</Typography>
              </>
            )}

            {profil.profil_beschreibung && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Über mich
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {profil.profil_beschreibung}
                </Typography>
              </>
            )}

            {profil.profil_abschluesse && profil.profil_abschluesse.length > 0 && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Abschlüsse und Qualifikationen
                </Typography>
                <Stack spacing={0.5}>
                  {profil.profil_abschluesse.map((a, i) => (
                    <Typography key={i} variant="body2">
                      {a.bezeichnung}
                      {a.art ? ` (${ABS_ART_LABEL[a.art] ?? a.art})` : ''}
                      {a.institution ? ` · ${a.institution}` : ''}
                      {a.jahr ? ` · ${a.jahr}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}

            {profil.profil_faehigkeiten && profil.profil_faehigkeiten.length > 0 && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Fähigkeiten
                </Typography>
                <Stack spacing={0.5}>
                  {profil.profil_faehigkeiten.map((f, i) => (
                    <Typography key={i} variant="body2">
                      {f.name}
                      {f.niveau ? ` · ${NIVEAU_LABEL[f.niveau] ?? f.niveau}` : ''}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Popover>
    </>
  );
};
